import { callApi, extractApiErrorMessage, hasSuccessFlag } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import ClientLogsService from "./client-logs.service";
import { buildPatientSearchConfig } from "./configs";

/**
 * Patient interface for API response
 */
export interface Patient {
  patient_id: number;
  first_name: string;
  last_name: string;
  dob: string;
  zipcode: string;
  email: string;
  practice_id: number;
}

/**
 * Patient search request parameters
 */
export interface PatientSearchParams {
  user_uuid: string;
  first_name: string;
  last_name: string;
  dob: string; // MM/DD/YYYY format
  zipcode: string; // 5-digit or ZIP+4 format
  email?: string;
  practice_id: number;
}

/**
 * Patient search response
 */
export interface PatientSearchResponse {
  success: boolean;
  data?: {
    foundPatient: boolean;
    transactionId: string;
  };
  error?: string;
}

interface BackendPatientSearchResponse {
  message?: boolean;
  transactionId?: string;
  error?: string;
}

/**
 * Search for patients using demographic information
 *
 * @param params - Patient search parameters
 * @returns Promise<PatientSearchResponse>
 */
export const searchPatients = async (
  params: PatientSearchParams,
): Promise<PatientSearchResponse> => {
  try {
    // Validate required parameters
    if (
      !params.user_uuid ||
      !params.first_name ||
      !params.last_name ||
      !params.dob ||
      !params.zipcode ||
      !params.practice_id
    ) {
      ClientLogsService.logError(
        params.user_uuid || "unknown",
        "patient-search.service.searchPatients",
        "Missing required parameters for patient search. API call aborted.",
        new Error("One or more required parameters are missing"),
        { errorType: "CallerError", functionName: "searchPatients" },
      );
      return {
        success: false,
        error: "Missing required parameters for patient search",
      };
    }

    // Make request to frontend Next.js API proxy route for patient search
    const { data: payload, response } = await callApi(buildPatientSearchConfig(params));

    if (!response.ok) {
      const errorMessage = extractApiErrorMessage(payload, response);

      ClientLogsService.logError(
        params.user_uuid,
        "patient-search.service.searchPatients",
        "Patient search API returned an error",
        new Error(`HTTP ${response.status}: ${response.statusText}`),
        {
          errorType: getFailureTypeForHttpStatus(response.status),
          dependencyName: "PatientSearchAPI",
          functionName: "searchPatients",
        },
      );

      return {
        success: false,
        error: errorMessage,
      };
    }

    if (hasSuccessFlag<PatientSearchResponse>(payload)) {
      return payload;
    }

    if (payload === null) {
      return { success: false, error: "Invalid JSON response" };
    }

    const backendPayload = payload as BackendPatientSearchResponse;

    return {
      success: true,
      data: {
        foundPatient: typeof backendPayload.message === "boolean" ? backendPayload.message : false,
        transactionId: backendPayload.transactionId || "",
      },
    };
  } catch (error) {
    ClientLogsService.logError(
      params.user_uuid || "unknown",
      "patient-search.service.searchPatients",
      "Error during patient search",
      error instanceof Error ? error : new Error("Unknown error"),
      {
        errorType: "DependencyFailure",
        dependencyName: "PatientSearchAPI",
        functionName: "searchPatients",
      },
    );
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "Unknown error occurred during patient search",
    };
  }
};
